import { ExcelExportDto, PowerpointExportDto } from '@ecosystem/export-api';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { useTrackChartTooltipEvent } from '../../analytics/trackChartInteractions.hook';
import { mapDataVizForChartType, trackPageElementClicked } from '../../analytics/utils';
import { EmptyState } from '../../common/no-data-warning/empty-state';
import { DataVizToggle } from '../../components/dataviz.toggle';
import { Instructions } from '../../content/instructions';
import { DataContentProps } from '../../content/types';
import { useMobile } from '../../context/mobile/mobile.provider';
import { ChartTitle } from '../../design/charts/chartTitle';
import { Loading } from '../../design/loading/Loading';
import GenericWarnings from '../../design/warning/GenericWarnings';
import { WarningsDisplay } from '../../design/warning/warningsDisplay';
import { ExportButton, ExportButtonProps } from '../../export/export.button';
import { ExportHideout } from '../../export/ExportHideout';
import { chartToPptData } from '../../export/utils/chartToPptData';
import { useCorePageState } from '../../page/provider';
import { ComponentHeaderActionType } from '../../page/types';
import {
  CollectionOptions,
  DataColumn,
  DataVizConfig,
  DataVizName,
  DataVizType,
  DataWarning,
  EventElementCategory,
  ExportFormat,
  ExportOptions,
} from '../../types';
import { useOnCollectionExportReady } from '../collection/useOnCollectionExportReady';
import { DISABLED_CHART_MESSAGE } from '../constants';
import { ChartData } from '../data/common';
import { useMobileChart } from '../mobileChart/mobileChart.hook';
import { MobileOverflowMenu } from '../mobileMenu/mobileOverflowMenu';
import { MultiKpiCollectionButton } from '../multiKpi/MultiKpiCollectionButton';
import { MultiKpiCollectionButtonProps } from '../multiKpi/types';
import { createChartRef, getDisplayAs, shouldDisableChart } from '../utils';
import { chartDataToExcelData } from './chartDataToExportData';
import { makeTimeSeriesData } from './data';
import { TimeSeriesChart } from './timeSeries.chart';
import { TimeSeriesTable } from './timeSeries.table';

const defaultDataVizType = DataVizType.chart;

export type TimeSeriesChartData = {
  series: ChartData;
  summary?: ChartData;
  exportSeries?: ChartData[];
};

function TimeSeries({
  data,
  isLoading,
  isEnrichmentLoading,
  instructions = {},
  componentSettings,
  paramsForData,
  paramsForContext,
  ComponentActionsContainer,
  collection,
  updateSettings,
  trackEvent,
  componentMetadata,
  componentHeadingTitle,
  proposition,
}: DataContentProps) {
  const [dataStamp, setDataStamp] = useState<number>();
  const [chartData, setChartData] = useState<TimeSeriesChartData>();
  const [xColumn, setXColumn] = useState<DataColumn>();
  const [yColumn, setYColumn] = useState<DataColumn>();
  const [warnings, setWarnings] = useState<DataWarning[]>();
  const [currency, setCurrency] = useState<string>();
  const chartSliceRefs = useRef<HTMLDivElement[]>([]);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportRefLength, setExportRefLength] = useState<number>(0);
  const { dsl, page, context, data: dataInfo, collection: collectionInfo, globalWarnings } = useCorePageState();
  const { isMobile } = useMobile();
  const { chartFullscreen, setChartFullscreen, MobileChartButton } = useMobileChart();
  const [isChartDisabled, setIsChartDisabled] = useState<boolean>(false);
  const [displayAs, setDisplayAs] = useState<DataVizType>(getDisplayAs(componentSettings));

  const sharedBrands = dsl?.settings?.values?.sharedBrands || [];

  const allColumns = data?.getAllColumns() ?? [];

  useOnCollectionExportReady({
    collection,
    dsl,
    chartSliceRefs,
    componentSettings,
    allColumns,
    componentHeadingTitle,
    refLength: exportRefLength,
  });

  const myInstructions = new Instructions(instructions, componentSettings);

  const xColumnName = myInstructions.getString('xColumn');
  const yColumnName = myInstructions.getString('yColumn');
  const exportOptions = myInstructions.getObject('export') as ExportOptions;
  const collectionOptions = myInstructions.getObject('collection') as CollectionOptions;

  const { trackTooltipEvent } = useTrackChartTooltipEvent(
    componentSettings,
    !!collection,
    collectionOptions?.chartName
  );

  const selectedKpis = componentSettings.kpi;

  const updateData = () => {
    if (!data) {
      return;
    }

    const chartData = makeTimeSeriesData(data, paramsForData, {
      xColumnName,
      yColumnName,
      sharedBrands,
    });
    if (chartData) {
      setCurrency(data.getCurrency());
      setWarnings(data.getDataWarnings(selectedKpis));
      setChartData(chartData);
      setDataStamp(data.stamp);
      setXColumn(data.getDataColumns().find(col => col.name === xColumnName));
      setYColumn(data.getDataColumns().find(col => col.name === yColumnName));

      const isDisabled = shouldDisableChart(chartData?.series?.dataSets);
      setIsChartDisabled(isDisabled);
      if (isDisabled) {
        setDisplayAs(DataVizType.table);
      }
    }
  };

  const handleDataVizChange = useCallback(
    (value: DataVizType) => {
      setDisplayAs(value);
      updateSettings({
        ...componentSettings,
        dataViz: [value],
      });
      trackPageElementClicked(trackEvent, mapDataVizForChartType(value), {
        elementCategory: mapDataVizForChartType(value) as EventElementCategory,
      });
    },
    [componentSettings, updateSettings, trackEvent]
  );

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (data) {
      collection?.onLoaded();
    }
  }, [data]);

  const canAggregate = useMemo(() => {
    return chartData ? chartData.series.yColumn.canAggregate : undefined;
  }, [chartData]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!data || data.stamp === dataStamp) {
      return;
    }
    updateData();
  }, [data]);

  useEffect(() => {
    if (!exportModalOpen) {
      chartSliceRefs.current = [];
    }
  }, [exportModalOpen]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    updateData();
  }, [yColumnName]);

  useEffect(() => {
    if (canAggregate === undefined) {
      return;
    }

    let requiredValue = displayAs || defaultDataVizType;

    if (!canAggregate && requiredValue === DataVizType.chart) {
      requiredValue = DataVizType.lineChart;
    }

    if (requiredValue !== displayAs) {
      handleDataVizChange(requiredValue);
    }
  }, [displayAs, canAggregate, handleDataVizChange]);

  const getExportData = async (exportFormat: ExportFormat): Promise<PowerpointExportDto | ExcelExportDto> => {
    const allColumns = [...data.getAvailableColumns(), ...data.getDataColumns()];
    switch (exportFormat) {
      case ExportFormat.Xlsx:
        return chartDataToExcelData(exportOptions, chartData, xColumn, yColumn, currency, globalWarnings);

      case ExportFormat.Pptx:
        return await chartToPptData(
          exportOptions,
          dsl,
          chartData.series.yColumn.label,
          chartSliceRefs.current,
          componentSettings,
          allColumns,
          componentHeadingTitle
        );

      default:
        break;
    }
  };

  const warningSettings = { appendKpis: true };

  const exportSeriesByChartType = (chartData: TimeSeriesChartData, displayAs: DataVizType) => {
    if (displayAs === DataVizType.lineChart) {
      return [chartData.series];
    } else {
      return chartData.exportSeries;
    }
  };
  if (collection && chartData && !chartData?.series.hasData) {
    collection.onExportReady(null);
    return <EmptyState />;
  }

  const dataVizConfigWithoutChart: DataVizConfig[] = [
    {
      type: DataVizType.lineChart,
      isDisabled: isChartDisabled,
      tooltipContent: DISABLED_CHART_MESSAGE,
      name: DataVizName.lineChart,
    },
    {
      type: DataVizType.table,
      isDisabled: false,
      name: DataVizName.table,
    },
  ];

  const dataVizConfigWithChart: DataVizConfig[] = [
    {
      type: DataVizType.chart,
      isDisabled: isChartDisabled,
      tooltipContent: DISABLED_CHART_MESSAGE,
      name: DataVizName.chart,
    },
    ...dataVizConfigWithoutChart,
  ];

  const dataVizConfigOptions = canAggregate ? dataVizConfigWithChart : dataVizConfigWithoutChart;

  const multiKpiCollectionButtonProps: MultiKpiCollectionButtonProps = {
    collectionOptions,
    collectionInfo,
    proposition,
    paramsForData,
    paramsForContext,
    context,
    page,
    dataInfo,
    componentSettings,
    isDisabled: isLoading,
  };

  const exportButtonProps: ExportButtonProps = {
    trackingSourceData: data,
    componentSettings,
    componentMetadata,
    disabled: isLoading,
    exportFormats: [ExportFormat.Xlsx, ExportFormat.Pptx],
    getExportData,
    exportContentRef: chartSliceRefs,
    onModalVisibilityChange: isVisible => setExportModalOpen(isVisible),
    trackEvent,
  };

  const renderActionsContainer = () => {
    return (
      <>
        <MultiKpiCollectionButton {...multiKpiCollectionButtonProps} actionType={ComponentHeaderActionType.Button} />
        {exportOptions && <ExportButton {...exportButtonProps} actionType={ComponentHeaderActionType.Button} />}
        {displayAs && canAggregate !== undefined && (
          <DataVizToggle options={dataVizConfigOptions} value={displayAs} onChange={handleDataVizChange} />
        )}
      </>
    );
  };

  const renderMobileActionsContainer = () => {
    return (
      <MobileOverflowMenu
        isLoading={isLoading}
        isAiEnabled={false}
        dataOptions={dataVizConfigOptions}
        canExportDataSet={!!exportOptions}
        canEditKpis={false}
        canToggleDataViz={!!displayAs && !!canAggregate}
        handleDataVizChange={handleDataVizChange}
        selectedDataViz={displayAs}
        multiKpiCollectionButtonProps={multiKpiCollectionButtonProps}
        exportButtonProps={exportButtonProps}
      />
    );
  };

  return (
    <>
      <WarningsDisplay warnings={warnings} className="pt-15" settings={warningSettings} />
      {chartData && ComponentActionsContainer && (
        <ComponentActionsContainer>
          {!isMobile ? renderActionsContainer() : renderMobileActionsContainer()}
        </ComponentActionsContainer>
      )}
      <div className={classNames({ 'chart-full-screen': chartFullscreen })}>
        {isMobile && !(isLoading || isEnrichmentLoading) && (
          <MobileChartButton chartFullscreen={chartFullscreen} setChartFullscreen={setChartFullscreen} />
        )}
        <div
          className={classNames('timeseries relative w-full max-w-full', {
            'pt-4': !chartFullscreen || displayAs === 'table',
          })}
        >
          <Loading isActive={isLoading || isEnrichmentLoading} withIndicator />
          {chartData && displayAs === DataVizType.table && (
            <>
              <TimeSeriesTable
                series={chartData.series}
                summary={chartData.summary}
                xColumn={xColumn}
                chartFullscreen={chartFullscreen}
              />
              {(exportModalOpen || collection) && (
                <>
                  {exportSeriesByChartType(chartData, displayAs).map((series, index, arr) => (
                    <ExportHideout key={index}>
                      <div ref={createChartRef(index, arr.length, setExportRefLength, chartSliceRefs)}>
                        {globalWarnings && <GenericWarnings warnings={globalWarnings} />}
                        <WarningsDisplay warnings={warnings} className="pt-15" settings={warningSettings} />
                        <TimeSeriesTable
                          series={series}
                          summary={chartData.summary}
                          xColumn={xColumn}
                          chartFullscreen={chartFullscreen}
                        />
                      </div>
                    </ExportHideout>
                  ))}
                </>
              )}
            </>
          )}
          {chartData && displayAs && displayAs !== DataVizType.table && (
            <>
              <ChartTitle title={chartData.series.yColumn.label} />
              <TimeSeriesChart
                displayAs={displayAs}
                series={chartData.series}
                summary={chartData.summary}
                currency={currency}
                trackTooltipEvent={trackTooltipEvent}
                chartFullscreen={chartFullscreen}
              />
              {(exportModalOpen || collection) && (
                <>
                  {exportSeriesByChartType(chartData, displayAs).map((series, index, arr) => (
                    <ExportHideout key={index}>
                      <div ref={createChartRef(index, arr.length, setExportRefLength, chartSliceRefs)}>
                        {globalWarnings && <GenericWarnings warnings={globalWarnings} />}
                        <WarningsDisplay warnings={warnings} className="pt-15" settings={warningSettings} />
                        <TimeSeriesChart
                          displayAs={displayAs}
                          series={series}
                          summary={chartData.summary}
                          currency={currency}
                          trackTooltipEvent={trackTooltipEvent}
                          chartFullscreen={chartFullscreen}
                        />
                      </div>
                    </ExportHideout>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
export { TimeSeries };
